/**
 * auth/msalConfig.js
 *
 * MSAL PublicClientApplication configuration for the AE Tracker SPA.
 * Uses delegated (user) auth — no client secret required.
 *
 * Azure App Registration:
 *   Client ID : c260cfba-eb5e-4133-a6e1-2ae35b2cf770
 *   Tenant ID : ae189c4c-0fbd-40a9-9d48-402527fc332f
 *   Type      : Single-page application (SPA)
 *   Redirect  : http://localhost:5173  (+ production URL once deployed)
 */

import { PublicClientApplication, LogLevel } from '@azure/msal-browser';

// ── MSAL core config ───────────────────────────────────────────────────────────

export const msalConfig = {
  auth: {
    clientId: 'c260cfba-eb5e-4133-a6e1-2ae35b2cf770',
    authority: 'https://login.microsoftonline.com/ae189c4c-0fbd-40a9-9d48-402527fc332f',
    // redirectUri must match what is registered in Azure Portal → Authentication → SPA
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // sessionStorage keeps the token alive for the browser tab only.
    // Switch to 'localStorage' if you want persistence across tabs.
    cacheLocation: 'sessionStorage',
    // Persist interim auth state in a cookie so the silent-renewal hidden iframe
    // can complete reliably (some browsers block storage access inside the iframe).
    // Hardens token refresh on page reload before the session has fully lapsed.
    storeAuthStateInCookie: true,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:   console.error('[MSAL]', message); break;
          case LogLevel.Warning: console.warn('[MSAL]',  message); break;
          case LogLevel.Info:    console.info('[MSAL]',  message); break;
          case LogLevel.Verbose: console.debug('[MSAL]', message); break;
        }
      },
      piiLoggingEnabled: false,
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
    },
  },
};

// ── Token scopes ───────────────────────────────────────────────────────────────

/**
 * Scopes requested on interactive login.
 * Sites.ReadWrite.All covers both read and write to SharePoint via Graph.
 * User.Read is needed to fetch the signed-in user's profile (/me).
 *
 * Note: These delegated scopes require admin consent in your tenant.
 * Grant consent in Azure Portal → API Permissions → Grant admin consent.
 */
export const loginRequest = {
  scopes: [
    'User.Read',
    'https://graph.microsoft.com/Sites.Read.All',
    'https://graph.microsoft.com/Sites.ReadWrite.All',
  ],
  // Pre-consent both SP and the Functions API so tokens are available silently.
  extraScopesToConsent: [
    'https://therapieclinic.sharepoint.com/AllSites.Read',
    'api://6509e101-f34d-4fae-bebd-e6fe615bd733/user_impersonation',
  ],
};

/**
 * Scopes for silent token acquisition (Graph API calls).
 * Must be a subset of what was consented to at login.
 * PIN reset mail uses the Functions API (app-only sender) — no Mail.Send here.
 */
export const graphRequest = {
  scopes: [
    'User.Read',
    'https://graph.microsoft.com/Sites.Read.All',
    'https://graph.microsoft.com/Sites.ReadWrite.All',
  ],
};

/**
 * Scopes for the AE Tracker Functions API.
 * Token audience = Functions app client ID.
 */
export const apiRequest = {
  scopes: ['api://6509e101-f34d-4fae-bebd-e6fe615bd733/user_impersonation'],
};

/**
 * Scopes for SharePoint REST API calls (different resource from Graph).
 * Used to fetch list item attachments via /_api/... endpoints.
 * Incremental consent — prompts once via popup on first use.
 */
export const spRequest = {
  scopes: ['https://therapieclinic.sharepoint.com/AllSites.Read'],
};

// ── Singleton MSAL instance ────────────────────────────────────────────────────
// Created once and shared across the app.
// main.jsx calls msalInstance.initialize() before first render.

export const msalInstance = new PublicClientApplication(msalConfig);
